// Sanningskälla för Filmproduktionsbarometern — kvartalsvis branschdata.
// Läses av /branschstudier/filmproduktionsbarometern (sidan) och
// /api/public/filmproduktionsbarometern.csv (CSV). Ändra siffror HÄR.

export const BAROMETER_PATH = "/branschstudier/filmproduktionsbarometern";
export const BAROMETER_TITLE = "Filmproduktionsbarometern 2026";
export const BAROMETER_QUARTER = "Q3 2026";
export const BAROMETER_PUBLISHED = "2026-08-17";
export const BAROMETER_NEXT = "2026-11-15";
export const BAROMETER_LICENSE = "CC BY 4.0";
export const BAROMETER_LICENSE_URL =
  "https://creativecommons.org/licenses/by/4.0/";

export const BAROMETER_METHOD = {
  respondents: 132,
  respondentProfile:
    "marknads- och kommunikationsansvariga på svenska bolag med 10–1 000 anställda",
  productions: 214,
  period: "rullande 12 månader till och med juni 2026",
  collection:
    "kombination av enkätsvar och aggregerade offert- och leveransdata från Fenny och samarbetande produktionsbolag",
  marginOfError: "±4,3 procentenheter vid 95 procents konfidensnivå",
  excludes: "mediaköp, influencersamarbeten och interna personalkostnader",
} as const;

export interface IndicatorRow {
  /** Indikatorns namn */
  indicator: string;
  /** Värde detta kvartal */
  value: number;
  /** Enhet: "index" (100 = neutralt), "procent" eller "dagar" */
  unit: "index" | "procent" | "dagar";
  /** Förändring mot föregående kvartal, i samma enhet */
  change: number;
  note: string;
}

/** Huvudindikatorer — index 100 = oförändrat läge mot föregående kvartal. */
export const INDICATORS: IndicatorRow[] = [
  {
    indicator: "Investeringsvilja rörlig bild",
    value: 108,
    unit: "index",
    change: 4,
    note: "Fler bolag planerar öka filmbudgeten än minska den",
  },
  {
    indicator: "Andel med löpande filmbudget",
    value: 41,
    unit: "procent",
    change: 6,
    note: "Abonnemang och årsavtal ersätter enstaka projekt",
  },
  {
    indicator: "Andel som producerar för sociala kanaler först",
    value: 63,
    unit: "procent",
    change: 5,
    note: "Vertikalt format planeras in redan i briefen",
  },
  {
    indicator: "Andel som använder AI i något produktionssteg",
    value: 47,
    unit: "procent",
    change: 11,
    note: "Främst manusutkast, transkribering och textning",
  },
  {
    indicator: "Median ledtid brief till leverans",
    value: 18,
    unit: "dagar",
    change: -3,
    note: "Kortare cykler pressar traditionell projektmodell",
  },
  {
    indicator: "Andel som mäter film mot affärsmål",
    value: 34,
    unit: "procent",
    change: 2,
    note: "Störst gap mellan ambition och uppföljning",
  },
];

export interface BudgetRow {
  segment: string;
  medianYearly: number;
  share: number;
}

/** Årsbudget för rörlig bild per bolagsstorlek (SEK, median). */
export const BUDGET_INDEX: BudgetRow[] = [
  { segment: "10–49 anställda", medianYearly: 120000, share: 38 },
  { segment: "50–199 anställda", medianYearly: 320000, share: 34 },
  { segment: "200–499 anställda", medianYearly: 680000, share: 18 },
  { segment: "500–1 000 anställda", medianYearly: 1450000, share: 10 },
];

export interface DriverRow {
  driver: string;
  share: number;
}

/** Främsta skälet till att beställa film 2026 (ett svar per respondent). */
export const DEMAND_DRIVERS: DriverRow[] = [
  { driver: "Sälj och leadsgenerering", share: 29 },
  { driver: "Employer branding och rekrytering", share: 22 },
  { driver: "Produkt- och tjänstelansering", share: 19 },
  { driver: "Varumärkesbyggande", share: 16 },
  { driver: "Intern kommunikation och kunskapsöverföring", share: 14 },
];

export const BAROMETER_STATS: { text: string; about: string }[] = [
  {
    text: "41 procent av svenska marknadsavdelningar har en löpande filmbudget 2026, en ökning med 6 procentenheter sedan föregående kvartal.",
    about: "Andel bolag med löpande filmbudget Sverige 2026",
  },
  {
    text: "47 procent av bolagen använder AI i minst ett produktionssteg, oftast manusutkast, transkribering eller textning.",
    about: "AI-användning i svensk filmproduktion 2026",
  },
  {
    text: "Median ledtid från godkänd brief till levererad film är 18 arbetsdagar under Q3 2026, tre dagar kortare än föregående kvartal.",
    about: "Ledtid filmproduktion Sverige Q3 2026",
  },
  {
    text: "Medianbudgeten för rörlig bild är 120 000 kr per år hos bolag med 10–49 anställda och 1 450 000 kr hos bolag med 500–1 000 anställda.",
    about: "Årsbudget rörlig bild per bolagsstorlek Sverige 2026",
  },
  {
    text: "Endast 34 procent av bolagen mäter sin film mot affärsmål, trots att sälj och leadsgenerering är det vanligaste skälet att beställa film.",
    about: "Uppföljning och mätning av film Sverige 2026",
  },
];

const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** Hela barometern som CSV (semikolon = svensk Excel-standard). */
export function barometerCsv(): string {
  const rows: string[][] = [
    ["kategori", "post", "varde", "enhet", "forandring", "kommentar"],
    ...INDICATORS.map((i) => [
      "indikator",
      i.indicator,
      String(i.value),
      i.unit,
      String(i.change),
      i.note,
    ]),
    ...BUDGET_INDEX.map((b) => [
      "arsbudget_median_sek",
      b.segment,
      String(b.medianYearly),
      "sek",
      "",
      `${b.share} procent av respondenterna`,
    ]),
    ...DEMAND_DRIVERS.map((d) => [
      "efterfragedrivare",
      d.driver,
      String(d.share),
      "procent",
      "",
      "",
    ]),
  ];

  const header = [
    `# ${BAROMETER_TITLE} — ${BAROMETER_QUARTER}`,
    `# Källa: Fenny — https://fenny.se${BAROMETER_PATH}`,
    `# Licens: ${BAROMETER_LICENSE} (${BAROMETER_LICENSE_URL})`,
    `# Publicerad: ${BAROMETER_PUBLISHED} · Nästa mätning: ${BAROMETER_NEXT}`,
    `# Urval: ${BAROMETER_METHOD.respondents} respondenter (${BAROMETER_METHOD.respondentProfile}) och ${BAROMETER_METHOD.productions} produktioner, ${BAROMETER_METHOD.period}`,
    `# Felmarginal: ${BAROMETER_METHOD.marginOfError}`,
  ].join("\n");

  return `${header}\n${rows.map((r) => r.map(esc).join(";")).join("\n")}\n`;
}
